import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import Button from 'components/Button/Button';
import { useModalRegistration } from 'views/ModalRegistryProvider';
import SimulatorAppFrag from './Simulator/SimulatorApp';
import { fetchQuery, graphql } from 'relay-runtime';
import { SimulatorImagesReviewModalQueryResponse } from './__generated__/SimulatorImagesReviewModalQuery.graphql';
import environment from 'api/environment';
import SimulatorHistoryFrag from './Simulator/SimulatorHistoryItem';
import moment from 'moment';
import Spinner from 'components/core/Spinner';
import { BarLoader, BeatLoader, DotLoader } from 'react-spinners';

const useStyles = createUseStyles((theme: Theme) => ({
  reviewModal: {
    position: 'fixed',
    top: 25,
    left: 25,
    background: '#18181B',
    zIndex: 10,
    borderRadius: 18,
    padding: '1.5rem',
    color: 'white',
    width: 'calc(100% - 50px)',
    height: 'calc(100% - 50px)',
    overflow: 'hidden',
    colorScheme: 'dark',

    // Animate in
    opactity: 0,
    animationName: '$animateIn',
    animationDuration: '0.3s',
    animationFillMode: 'forwards'
  },
  '@keyframes animateIn': {
    from: {
      opacity: 0
    },
    to: {
      opacity: 1
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 20
  },
  header__info: {},
  header__title: {
    margin: 0,
    fontSize: '1.1rem'
  },
  header__subtitle: {
    padding: 0,
    margin: 0,
    color: '#A0A0A0',
    fontSize: '0.8rem'
  },
  content: {
    display: 'flex',
    gap: '1rem',
    height: 'calc(100% - 60px)'
  },
  imagesSeen: {
    background: '#1E1E21',
    border: '1px solid #343436',
    borderRadius: 12,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minWidth: '22rem',
    overflow: 'hidden'
  },
  imagesSeen__images: {
    overflowY: 'auto',
    height: '100%',
    padding: 20,
    paddingTop: 0,
    outline: 'none'
  },
  imageSeen__heading: {
    padding: 20
  },
  imagesSeen__title: {
    margin: 0,
    fontSize: '1rem'
  },
  imagesSeen__description: {
    fontSize: '0.8rem',
    color: '#A0A0A0',
    padding: 0,
    margin: 0
  },
  imagesSeen__date: {
    margin: 0,
    padding: 0,
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#969696',
    marginBottom: '1rem',
    marginTop: '1.3rem'
  },
  imagesSeen__imageBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem'
  },
  simulator: {
    width: '100%'
  },
  backgroundHider: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(5px)',
    zIndex: 10
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%'
  }
}));

const query = graphql`
  query SimulatorImagesReviewModalQuery($uuid: UUID!, $monthID: String!) {
    delegate(uuid: $uuid) {
      firstName
      simulator {
        imageHistory(monthID: $monthID, page: { limit: 300 }) {
          edges {
            uuid
            start
            ...SimulatorApp_imageResult
            ...SimulatorHistoryItem_result
          }
          pageInfo {
            limit
            offset
            given
            total
          }
        }
      }
    }
  }
`;

type Image = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        SimulatorImagesReviewModalQueryResponse['delegate']
      >['simulator']
    >['imageHistory']['edges']
  >[number]
>;

type Props = {
  delegateUUID: string;
  month: moment.Moment; // Any date in the month to be reviewed
  onClose: () => void;
};

function SimulatorImagesReviewModal(props: Props) {
  const classes = useStyles();

  const identifyBoxRef = React.useRef(null);
  const [delegate, setDelegate] = React.useState<
    SimulatorImagesReviewModalQueryResponse['delegate']
  >(null);
  const [selectedImage, setSelectedImage] = React.useState<Image | null>(null);

  React.useEffect(() => {
    const variables = {
      uuid: props.delegateUUID,
      monthID: moment(props.month).format('YYYY-MM')
    };

    fetchQuery(environment, query, variables).then(
      (data: SimulatorImagesReviewModalQueryResponse) => {
        setDelegate(data.delegate);
        setSelectedImage(
          data.delegate?.simulator?.imageHistory?.edges?.[0] ?? null
        );
      }
    );
  }, [props.delegateUUID]);

  // Renders nothing until the query lands, so only count it once it's on screen.
  useModalRegistration(!!delegate?.simulator);

  if (!delegate?.simulator) {
    return null;
  }

  const historyImages: {
    date: string;
    images: Image[];
  }[] = [];

  // Put images into bins depending on the day they were taken on
  delegate.simulator.imageHistory.edges?.forEach((image) => {
    if (!image) return;

    const date = moment(image.start);

    const key = date.format('DD MMM');

    const existingHistoryImage = historyImages.find(
      (item) => item.date === key
    );

    if (!existingHistoryImage) {
      historyImages.push({
        date: key,
        images: [image]
      });
    } else {
      existingHistoryImage.images.push(image);
    }
  });

  const onImageKeyDown = (e: React.KeyboardEvent) => {
    // Create a flat array of all images from historyImages
    let flatImages: Image[] = [];
    historyImages.forEach((history) => {
      flatImages = [...flatImages, ...history.images];
    });

    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const currentIndex = flatImages.findIndex(
        (image) => image.uuid === selectedImage?.uuid
      );
      const previousIndex = currentIndex - 1;
      if (previousIndex >= 0) {
        const previousImage = flatImages[previousIndex];
        setSelectedImage(previousImage);
      }
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      const currentIndex = flatImages.findIndex(
        (image) => image.uuid === selectedImage?.uuid
      );
      const nextIndex = currentIndex + 1;
      if (nextIndex < flatImages.length) {
        const nextImage = flatImages[nextIndex];
        setSelectedImage(nextImage);
      }
    }
  };

  return (
    <>
      <div className={classes.backgroundHider} onClick={props.onClose} />
      <div className={classes.reviewModal}>
        {!delegate && (
          <div className={classes.loader}>
            <BeatLoader color="white" size={12} />
          </div>
        )}
        {delegate && (
          <>
            <div className={classes.header}>
              <div className={classes.header__info}>
                <h3 className={classes.header__title}>
                  Review {delegate?.firstName}'s 6x6 images
                </h3>
                <p className={classes.header__subtitle}>Sept 2024</p>
              </div>
              <Button
                archetype="submit"
                onClick={() => {
                  props.onClose();
                }}
              >
                Close review
              </Button>
            </div>
            <div className={classes.content}>
              <div className={classes.imagesSeen}>
                <div className={classes.imageSeen__heading}>
                  <h4 className={classes.imagesSeen__title}>Images seen</h4>
                  <p className={classes.imagesSeen__description}>
                    These are all the images seen by {delegate?.firstName} this
                    month
                  </p>
                </div>
                <div
                  className={classes.imagesSeen__images}
                  onKeyDown={onImageKeyDown}
                  tabIndex={0}
                >
                  {historyImages.map((history) => {
                    return (
                      <div>
                        <h4 className={classes.imagesSeen__date}>
                          {history.date}
                        </h4>
                        <div className={classes.imagesSeen__imageBlock}>
                          {history.images.map((image) => {
                            return (
                              <SimulatorHistoryFrag
                                key={image?.start}
                                result={image}
                                selected={image.uuid === selectedImage?.uuid}
                                onClick={() => {
                                  setSelectedImage(image);
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={classes.simulator}>
                {selectedImage && (
                  <SimulatorAppFrag
                    imageResult={selectedImage}
                    sidebarHidden={false}
                    identRef={identifyBoxRef}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default SimulatorImagesReviewModal;
